/**
 * @file Maps theme tokens to CSS custom properties, per color scheme.
 *
 * Scheme-invariant tokens live in `variables`; everything color-related is
 * emitted under `light`/`dark` (Mantine scopes those to
 * [data-mantine-color-scheme]). The var()-backed `semanticColors` and
 * `shadows` exports resolve against these.
 */
import { semanticPalette } from './semanticColors';
import { shadowValues } from './shadows';
import { motion } from './motion';
import { charcoal } from './colors';
import { layout } from './layout';

const flatten = (obj, prefix) =>
  Object.entries(obj).reduce(
    (acc, [key, value]) =>
      typeof value === 'object' && value !== null
        ? { ...acc, ...flatten(value, `${prefix}-${key}`) }
        : { ...acc, [`${prefix}-${key}`]: value },
    {}
  );

const schemeVars = (scheme) => ({
  ...flatten(semanticPalette[scheme], '--dt'),
  ...flatten(shadowValues[scheme], '--dt-shadow'),
  '--mantine-color-body': semanticPalette[scheme].surface.base,
  '--mantine-color-text': semanticPalette[scheme].text.primary
});

export const cssVariablesResolver = (theme) => ({
  variables: {
    '--dt-color-coral': theme.colors.coral[6],
    '--dt-color-teal': theme.colors.teal[6],
    '--dt-color-sand': theme.colors.sand[3],
    '--dt-color-charcoal': theme.colors.charcoal[8],
    '--dt-radius-control': '8px',
    '--dt-radius-surface': '16px',
    '--dt-radius-media': '20px',
    // Figures on record (dates, prices, counts) in the headings family: never under 1.125rem.
    '--dt-numeral-sm': '1.125rem',
    '--dt-numeral-md': '1.5rem',
    '--dt-numeral-lg': '2rem',
    '--dt-motion-fast': motion.duration.fast,
    '--dt-motion-standard': motion.duration.standard,
    '--dt-motion-easing': motion.easing.standard,
    '--dt-motion-slow': motion.duration.slow,
    '--dt-motion-hero': motion.duration.hero,
    '--dt-motion-decel': motion.easing.decel,
    '--dt-motion-accel': motion.easing.accel,
    '--dt-z-modal': '1500',
    '--dt-z-toast': '1700',
    '--dt-touch-target': layout.touchTarget,
    '--dt-gutter': layout.gutter,
    '--dt-content-max-narrow': layout.contentMaxNarrow,
    '--dt-content-max': layout.contentMax,
    '--dt-content-max-wide': layout.contentMaxWide,
    '--dt-layout-sidebar-lg': layout.sidebarLg,
    '--dt-layout-reader-lg': layout.readerLg,
    '--dt-layout-rail-w': layout.railW,
    '--dt-layout-editor-dialog-w': layout.editorDialogW,
    '--dt-sticky-cta-h': layout.stickyCtaH
  },
  light: schemeVars('light'),
  dark: {
    ...schemeVars('dark'),
    // Flip the charcoal ink ramp so c="charcoal.X" props stay readable in dark.
    // Charcoal-as-background usages must pin explicit hex instead.
    ...Object.fromEntries(charcoal.map((_, i) => [`--mantine-color-charcoal-${i}`, charcoal[9 - i]]))
  }
});
